import { useSyncExternalStore } from 'react';
import type { MenuItemForOrder } from './customer.service';

export interface MenuCartLine {
  item: MenuItemForOrder;
  quantity: number;
}

/** One restaurant's bucket inside the menu cart. */
export interface RestaurantCart {
  businessId: string;
  businessName: string;
  lines: MenuCartLine[];
}

export function cartItemCount(cart: RestaurantCart): number {
  return cart.lines.reduce((sum, l) => sum + l.quantity, 0);
}

export function cartSubtotal(cart: RestaurantCart): number {
  return cart.lines.reduce((sum, l) => sum + l.item.price * l.quantity, 0);
}

export function cartDisplayName(cart: RestaurantCart): string {
  return cart.businessName || 'Restaurant';
}

function resolveBusinessId(item: MenuItemForOrder): string {
  return typeof item.business === 'object' && item.business
    ? item.business._id
    : String(item.business || '');
}

function resolveBusinessName(item: MenuItemForOrder): string {
  return typeof item.business === 'object' && item.business ? item.business.name : '';
}

/**
 * A multi-restaurant cart of regular menu items, for the standalone
 * "Restaurants" ordering flow (independent of the surprise-box cart in
 * customer.service.ts / Cart.tsx).
 *
 * Purely local/in-memory for this browser tab. Items are bucketed per
 * restaurant, so adding from a second restaurant never disturbs what's
 * already in the cart. At checkout each bucket is sent as its own
 * `POST /api/customer/orders` call via `customerService.createMenuOrder`,
 * giving every restaurant its own pickup code and its own order status.
 *
 * State is immutable: every mutation swaps in fresh arrays so
 * `useSyncExternalStore` sees a new snapshot.
 */
class MenuCartStore {
  private carts: RestaurantCart[] = [];
  private listeners = new Set<() => void>();

  private emit() {
    this.listeners.forEach((listener) => listener());
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): RestaurantCart[] => this.carts;

  cartFor(businessId: string): RestaurantCart | undefined {
    return this.carts.find((c) => c.businessId === businessId);
  }

  /** Menu item ids are globally unique, so a bare id finds a line anywhere. */
  quantityOf(menuItemId: string): number {
    for (const cart of this.carts) {
      const match = cart.lines.find((l) => l.item._id === menuItemId);
      if (match) return match.quantity;
    }
    return 0;
  }

  itemCount(): number {
    return this.carts.reduce((sum, c) => sum + cartItemCount(c), 0);
  }

  total(): number {
    return this.carts.reduce((sum, c) => sum + cartSubtotal(c), 0);
  }

  /** Add one unit of `item` to its own restaurant's bucket. */
  addItem(item: MenuItemForOrder) {
    const businessId = resolveBusinessId(item);
    const existingCart = this.cartFor(businessId);

    if (!existingCart) {
      this.carts = [
        ...this.carts,
        {
          businessId,
          businessName: resolveBusinessName(item),
          lines: [{ item, quantity: 1 }],
        },
      ];
      this.emit();
      return;
    }

    const hasLine = existingCart.lines.some((l) => l.item._id === item._id);
    const lines = hasLine
      ? existingCart.lines.map((l) =>
          l.item._id === item._id ? { ...l, quantity: l.quantity + 1 } : l
        )
      : [...existingCart.lines, { item, quantity: 1 }];

    this.carts = this.carts.map((c) =>
      c.businessId === businessId ? { ...c, lines } : c
    );
    this.emit();
  }

  /** Remove one unit; drops the line at zero and the bucket when it empties. */
  removeItem(menuItemId: string) {
    const owner = this.carts.find((c) =>
      c.lines.some((l) => l.item._id === menuItemId)
    );
    if (!owner) return;

    const lines = owner.lines
      .map((l) =>
        l.item._id === menuItemId ? { ...l, quantity: l.quantity - 1 } : l
      )
      .filter((l) => l.quantity > 0);

    this.carts =
      lines.length === 0
        ? this.carts.filter((c) => c.businessId !== owner.businessId)
        : this.carts.map((c) =>
            c.businessId === owner.businessId ? { ...c, lines } : c
          );
    this.emit();
  }

  /** Drop one restaurant's whole bucket, leaving the rest of the cart alone. */
  clearRestaurant(businessId: string) {
    if (!this.cartFor(businessId)) return;
    this.carts = this.carts.filter((c) => c.businessId !== businessId);
    this.emit();
  }

  clear() {
    this.carts = [];
    this.emit();
  }
}

export const menuCartStore = new MenuCartStore();

/** React hook: re-renders the component whenever the cart changes. */
export function useMenuCart() {
  const carts = useSyncExternalStore(menuCartStore.subscribe, menuCartStore.getSnapshot);
  return {
    carts,
    /** Every line across every restaurant, flattened. */
    lines: carts.flatMap((c) => c.lines),
    restaurantCount: carts.length,
    itemCount: menuCartStore.itemCount(),
    total: menuCartStore.total(),
    cartFor: (businessId: string) => menuCartStore.cartFor(businessId),
    itemCountFor: (businessId: string) => {
      const cart = menuCartStore.cartFor(businessId);
      return cart ? cartItemCount(cart) : 0;
    },
    subtotalFor: (businessId: string) => {
      const cart = menuCartStore.cartFor(businessId);
      return cart ? cartSubtotal(cart) : 0;
    },
    quantityOf: (menuItemId: string) => menuCartStore.quantityOf(menuItemId),
    addItem: (item: MenuItemForOrder) => menuCartStore.addItem(item),
    removeItem: (menuItemId: string) => menuCartStore.removeItem(menuItemId),
    clearRestaurant: (businessId: string) => menuCartStore.clearRestaurant(businessId),
    clear: () => menuCartStore.clear(),
  };
}
